import React, { useState, useCallback } from 'react';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { MOCK_AUDIT_LOGS } from '@/mocks/database';
import { AuditLog } from '@/types/domain';
import { formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge';
import { processQuery } from '@/services/mock/pagination';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { Search, ShieldAlert } from 'lucide-react';

export const AuditListPage: React.FC = () => {
  const [search, setSearch] = useState('');

  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      const res = processQuery(MOCK_AUDIT_LOGS, {
        search,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      return res;
    },
    [search]
  );

  const {
    items: auditLogs,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    total,
    sentinelRef,
  } = useInfiniteScroll<AuditLog>({
    fetchFn,
    params: { search },
    pageSize: 25,
  });

  const columns: ColumnDef<AuditLog>[] = [
    { header: 'Audit ID', accessorKey: 'id', cell: (row) => <span className="font-mono text-xs text-primary">{row.id}</span> },
    { header: 'User', cell: (row) => <span className="font-bold text-xs text-foreground">{row.userName || 'System User'}</span> },
    { header: 'Action', accessorKey: 'action', cell: (row) => <Badge variant="outline" className="text-[10px] font-bold">{row.action}</Badge> },
    { header: 'Target Entity', cell: (row) => <span className="font-mono text-xs">{row.entityType} ({row.entityId})</span> },
    { header: 'Details', accessorKey: 'details', cell: (row) => <span className="text-xs text-foreground-secondary truncate max-w-xs">{row.details}</span> },
    { header: 'IP Address', accessorKey: 'ipAddress', cell: (row) => <span className="font-mono text-xs text-foreground-muted">{row.ipAddress}</span> },
    { header: 'Timestamp', accessorKey: 'timestamp', cell: (row) => formatDate(row.timestamp) },
  ];

  return (
    <div className="space-y-6 font-heading">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5">
            <ShieldAlert className="h-7 w-7 text-primary" /> Immutable Audit Trail
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1">
            Compliance activity logs tracking approvals, status mutations, 3-way matches, and administrative actions across 1,000+ records.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 rounded-2xl border border-border bg-card shadow-2xs">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search action, user, entity ID, IP address..."
            className="w-full h-9 pl-10 pr-4 text-xs font-medium rounded-xl border border-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      <DataTable
        data={auditLogs}
        columns={columns}
        isLoading={isLoading}
        pageSize={25}
        totalCount={total}
        infiniteScroll={true}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sentinelRef={sentinelRef}
      />
    </div>
  );
};
